import { computed } from "vue";
import { usePage } from "@inertiajs/vue3";
import { useTranslations } from "@/composables/useTranslations";
import {
    MCP_CONNECT_URL,
    MCP_CHATGPT_STEP_KEYS,
    MCP_CLAUDE_STEP_KEYS,
    MCP_REQUIREMENT_KEYS,
} from "@/constants/mcpServer";

type Translate = (key: string, params?: Record<string, unknown>) => string;

export interface IAiPageContext {
    page: string;
    title: string;
    url: string;
    data?: Record<string, unknown>;
}

interface IResolvedContext {
    page: string;
    title: string;
    data?: Record<string, unknown>;
}

interface IPageAdapter {
    id: string;
    resolve: (props: Record<string, unknown>, path: string) => IResolvedContext;
}

const pick = (
    source: unknown,
    keys: string[],
): Record<string, unknown> | undefined => {
    if (typeof source !== "object" || source === null) {
        return undefined;
    }
    const record = source as Record<string, unknown>;
    const out: Record<string, unknown> = {};
    for (const key of keys) {
        if (record[key] !== undefined) {
            out[key] = record[key];
        }
    }
    return Object.keys(out).length > 0 ? out : undefined;
};

const compact = (value: Record<string, unknown>): Record<string, unknown> => {
    const out: Record<string, unknown> = {};
    for (const [key, val] of Object.entries(value)) {
        if (val !== null && val !== undefined) {
            out[key] = val;
        }
    }
    return out;
};

const decode = (segment: string | undefined): string | undefined => {
    if (segment === undefined || segment === "") {
        return undefined;
    }
    try {
        return decodeURIComponent(segment);
    } catch {
        return segment;
    }
};

// The on-screen pass/fail headline for a report drill-down. Kept compact (totals
// + rate only, never the full event table) so the assistant knows what the user
// is looking at and can pick tool parameters; authoritative detail still comes
// from the report tools.
const reportHeadline = (reports: unknown): Record<string, unknown> => {
    if (typeof reports !== "object" || reports === null) {
        return {};
    }
    const total = (reports as Record<string, unknown>).total;
    if (typeof total !== "object" || total === null) {
        return {};
    }
    const record = total as Record<string, unknown>;
    const pass = Number(record.pass ?? 0);
    const fail = Number(record.fail ?? 0);
    const sum = Number(record.sum ?? pass + fail);

    return {
        total: { pass, fail, sum },
        complianceRate: sum > 0 ? Math.round((pass / sum) * 1000) / 10 : 0,
    };
};

const reportGeo = (geoItem: unknown): Record<string, unknown> | undefined =>
    pick(geoItem, ["city", "region", "country", "org"]);

// The active table filter (search / sort / pagination) exactly as the server
// applied it — so the assistant sees the same filtered list the user does.
const tableFilter = (table: unknown): Record<string, unknown> | undefined =>
    pick(table, [
        "q",
        "sort_by",
        "sort_dir",
        "parked",
        "subdomain_view",
        "per_page",
        "current_page",
    ]);

const ROWS_LIMIT = 15;

// A compact digest of the table rows the user actually sees: whitelisted
// fields only, capped at ROWS_LIMIT, always accompanied by completeness
// metadata (shown / total / page) so the assistant never mistakes the visible
// slice for the whole dataset.
const visibleRows = (
    source: unknown,
    mapRow: (row: unknown) => Record<string, unknown> | undefined,
    pagination?: unknown,
): Record<string, unknown> => {
    if (!Array.isArray(source) || source.length === 0) {
        return {};
    }
    const rows: Record<string, unknown>[] = [];
    for (const raw of source) {
        const mapped = mapRow(raw);
        if (mapped !== undefined && Object.keys(mapped).length > 0) {
            rows.push(mapped);
        }
        if (rows.length >= ROWS_LIMIT) {
            break;
        }
    }
    if (rows.length === 0) {
        return {};
    }
    const pageInfo =
        pick(pagination, ["total", "current_page", "last_page"]) ?? {};
    const total =
        typeof pageInfo.total === "number" ? pageInfo.total : source.length;

    return {
        rows,
        rows_meta: compact({
            shown: rows.length,
            total,
            truncated: rows.length < total,
            page: pageInfo.current_page,
            pages: pageInfo.last_page,
        }),
    };
};

const pickRow =
    (keys: string[]) =>
    (row: unknown): Record<string, unknown> | undefined =>
        pick(row, keys);

// A domain overview row (dashboard cards, reports index table): the per-domain
// report totals live in the nested report_current object, flattened here.
const domainReportRow = (row: unknown): Record<string, unknown> | undefined => {
    if (typeof row !== "object" || row === null) {
        return undefined;
    }
    const record = row as Record<string, unknown>;
    const report =
        typeof record.report_current === "object" &&
        record.report_current !== null
            ? (record.report_current as Record<string, unknown>)
            : {};

    return compact({
        domain: record.domain,
        policy: record.dmarcPolicy,
        sum: report.sum,
        pass: report.pass,
        fail: report.fail,
    });
};

const REPORT_SOURCE_ROW_KEYS = [
    "source",
    "ip",
    "sum",
    "pass",
    "fail",
    "complientPercent",
    "spf_valid",
    "spf_invalid",
    "dkim_valid",
    "dkim_invalid",
];

const dmarcPolicyOf = (reportedDomain: unknown): string | undefined => {
    if (typeof reportedDomain !== "object" || reportedDomain === null) {
        return undefined;
    }
    const policy = (reportedDomain as Record<string, unknown>).dmarcPolicy;

    return typeof policy === "string" ? policy : undefined;
};

// The MCP server page is static onboarding and passes no props, so there is
// nothing to snapshot from `props`. Instead we hand the assistant the actual
// connect instructions the page renders — from the same i18n keys — so it can
// walk a user through connecting reliably. The model cannot open URLs, so the
// concrete steps must live in the payload text, not behind a docs link.
const buildMcpServerContext = (t: Translate): Record<string, unknown> => ({
    connectUrl: MCP_CONNECT_URL,
    requirements: {
        chatgpt: t(MCP_REQUIREMENT_KEYS.chatgpt),
        claude: t(MCP_REQUIREMENT_KEYS.claude),
    },
    chatgptSteps: MCP_CHATGPT_STEP_KEYS.map((key) => t(key)),
    claudeSteps: MCP_CLAUDE_STEP_KEYS.map((key) => t(key)),
});

// Resolves the report detail family (domain / source / ip / dmarc-management)
// from the URL segments so the assistant knows exactly what the user is viewing.
const resolveReportDetail = (
    props: Record<string, unknown>,
    path: string,
): IResolvedContext => {
    const segments = path.split("/").filter((s) => s !== "");
    const domain = decode(segments[1]);
    const kind = segments[2];
    const shared = {
        domain,
        dateRange: props.dateRange,
        table: tableFilter(props.table),
    };
    const headline = reportHeadline(props.reports);

    if (kind === "ip") {
        return {
            page: "reports.ip",
            title: "Report detail – IP",
            data: {
                ...shared,
                source: decode(segments[3]),
                ip: decode(segments[4]),
                ...headline,
                geo: reportGeo(props.geoItem),
                ...visibleRows(
                    props.tableRows,
                    pickRow(REPORT_SOURCE_ROW_KEYS),
                    props.pagination,
                ),
            },
        };
    }
    if (kind === "source") {
        return {
            page: "reports.source",
            title: "Report detail – source",
            data: {
                ...shared,
                source: decode(segments[3]),
                ...headline,
                ...visibleRows(
                    props.tableRows,
                    pickRow(REPORT_SOURCE_ROW_KEYS),
                    props.pagination,
                ),
            },
        };
    }
    if (kind === "dmarc-management") {
        return {
            page: "reports.dmarc-management",
            title: "DMARC management",
            data: { domain },
        };
    }
    return {
        page: "reports.show",
        title: "Report detail",
        data: {
            ...shared,
            statistics: props.statistics,
            dmarcPolicy: dmarcPolicyOf(props.reportedDomain),
            ...visibleRows(
                props.tableRows,
                pickRow(REPORT_SOURCE_ROW_KEYS),
                props.pagination,
            ),
        },
    };
};

// Ordered by specificity: report detail (with a domain segment) is matched
// before the generic "/reports" index.
const ADAPTERS: IPageAdapter[] = [
    {
        id: "/dashboard",
        resolve: (p) => ({
            page: "dashboard",
            title: "Dashboard",
            data: {
                dateRange: p.dateRange,
                statistics: p.statistics,
                ...visibleRows(p.activeDomains, domainReportRow),
            },
        }),
    },
    {
        id: "/reports/",
        resolve: resolveReportDetail,
    },
    {
        id: "/reports",
        resolve: (p) => ({
            page: "reports",
            title: "Reports",
            data: {
                dateRange: p.dateRange,
                table: tableFilter(p.table),
                statistics: p.statistics,
                ...visibleRows(p.activeDomains, domainReportRow, p.pagination),
            },
        }),
    },
    {
        id: "/domains",
        resolve: (p) => ({
            page: "domains",
            title: "Domains",
            data: {
                ...(pick(p, [
                    "totalDomainsCount",
                    "totalActiveDomains",
                    "totalInactiveDomains",
                    "totalParkedDomainsCount",
                    "totalEmailsReported",
                    "pagination",
                ]) ?? {}),
                table: tableFilter(p.table),
                ...visibleRows(
                    p.domains,
                    pickRow([
                        "domain",
                        "active",
                        "parked",
                        "dmarcPolicy",
                        "isSubdomain",
                        "subdomainsCount",
                    ]),
                    p.pagination,
                ),
            },
        }),
    },
    {
        id: "/blacklists",
        resolve: (p) => ({
            page: "blacklists",
            title: "Blacklists",
            data: {
                ipsUsedCount: p.ipsUsedCount,
                ipsBlacklistedCount: p.ipsBlacklistedCount,
                table: p.table,
            },
        }),
    },
    {
        id: "/alerts/overview",
        resolve: (p) => ({
            page: "alerts",
            title: "Alerts overview",
            data: {
                notificationsEnabled: p.notificationsEnabled,
                table: p.table,
                pagination: p.pagination,
            },
        }),
    },
    {
        id: "/dns-tools",
        resolve: () => ({ page: "dns-tools", title: "DNS tools" }),
    },
    {
        id: "/integrations/mcp-server",
        resolve: () => ({ page: "mcp-server", title: "MCP server settings" }),
    },
];

export function useAiPageContext() {
    const page = usePage();
    const { t } = useTranslations();

    const path = computed(() => page.url.split("?")[0]);

    // Demo mode renders fixture data into page props; demo data and AI are
    // strictly separate, so no page context is offered at all while it is on.
    const adapter = computed(() =>
        page.props.demoMode === true
            ? null
            : (ADAPTERS.find((a) => path.value.startsWith(a.id)) ?? null),
    );

    const available = computed(() => adapter.value !== null);

    const build = (): IAiPageContext | null => {
        if (adapter.value === null) {
            return null;
        }
        const props = page.props as Record<string, unknown>;
        const resolved = adapter.value.resolve(props, path.value);
        const data =
            resolved.page === "mcp-server"
                ? buildMcpServerContext(t)
                : compact(resolved.data ?? {});
        return {
            page: resolved.page,
            title: resolved.title,
            url: page.url,
            data: Object.keys(data).length > 0 ? data : undefined,
        };
    };

    const humanTitle = computed(() =>
        adapter.value === null
            ? ""
            : adapter.value.resolve(
                  page.props as Record<string, unknown>,
                  path.value,
              ).title,
    );

    return { available, humanTitle, build };
}
